import ErrorComponent from "@/components/atoms/ErrorComponent";
import { getReelRoleName, isStrictAdmin } from "@/helpers/UserHelpers";
import { COMMISSION_ROLES, UserRoleType } from "@/interfaces/IUser";
import {
  useGetUserCommissionsQuery,
  useUpdateUserCommissionsMutation,
} from "@/services/users/commissions";
import { selectCurrentUser } from "@/services/slices/authSlice";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { FC, useEffect } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Placeholder,
  Row,
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { ToastContentProps, toast } from "react-toastify";

type CommissionsForm = Record<UserRoleType, number>;

const UserCommissionsCard: FC<{ id: string }> = ({ id }) => {
  const currentUser = useSelector(selectCurrentUser);
  const { data, isLoading, isError, error } = useGetUserCommissionsQuery(id);
  const [updateUserCommissionsAsync, { isLoading: isSaving }] =
    useUpdateUserCommissionsMutation();

  const { register, handleSubmit, reset } = useForm<CommissionsForm>();

  useEffect(() => {
    if (data) {
      reset(
        COMMISSION_ROLES.reduce((acc, role) => {
          acc[role] = data.find((c) => c.role === role)?.commission ?? 0;
          return acc;
        }, {} as CommissionsForm)
      );
    }
  }, [data]);

  if (!isStrictAdmin(currentUser)) {
    return null;
  }

  if (isLoading) {
    return (
      <Card className="mt-3">
        <Card.Header>
          <h6 className="mb-0">Commissions par rôle</h6>
        </Card.Header>
        <Card.Body>
          <Placeholder as="div" animation="glow">
            <Row className="g-3">
              {COMMISSION_ROLES.map((role) => (
                <Col md={6} key={role}>
                  <Placeholder xs={5} className="mb-2" />
                  <Placeholder xs={12} size="lg" />
                </Col>
              ))}
            </Row>
            <Placeholder.Button
              variant="success"
              xs={2}
              className="mt-3"
            />
          </Placeholder>
        </Card.Body>
      </Card>
    );
  } else if (isError) {
    return <ErrorComponent error={error} returnButton />;
  }

  const onSubmit = (values: CommissionsForm) => {
    toast.promise(
      updateUserCommissionsAsync([
        id,
        {
          commissions: COMMISSION_ROLES.map((role) => ({
            role,
            commission: Number(values[role]) || 0,
          })),
        },
      ]).unwrap(),
      {
        pending: "Enregistrement des commissions...",
        success: "Les commissions ont bien été mises à jour 🤩",
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            if (data?.status === 422) {
              return "Les valeurs saisies semblent incorrectes (0 à 100).";
            }
            return "Une erreur est survenue";
          },
        },
      }
    );
  };

  return (
    <Card className="mt-3">
      <Card.Header>
        <h6 className="mb-0">Commissions par rôle</h6>
      </Card.Header>
      <Card.Body>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row className="g-3">
            {COMMISSION_ROLES.map((role) => (
              <Col md={6} key={role}>
                <Form.Group>
                  <Form.Label>Commission {getReelRoleName([role])}</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min={0}
                      max={100}
                      {...register(role, {
                        min: 0,
                        max: 100,
                        valueAsNumber: true,
                      })}
                    />
                    <InputGroup.Text>%</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              </Col>
            ))}
          </Row>
          <div className="mt-3">
            <Button type="submit" variant="success" disabled={isSaving}>
              Enregistrer
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default UserCommissionsCard;
